/* eslint-disable jsx-a11y/interactive-supports-focus */
/* eslint-disable jsx-a11y/click-events-have-key-events */

import { Container } from '@audio-network/componentlibrary'
import { Search } from '@audio-network/componentlibrary/icons'
import clsx from 'clsx'
import { useTranslation } from 'next-i18next'
import { useCallback } from 'react'

import useSearchSelectorStore from '@/stores/searchSelectorStore'

import { SearchSelector } from '../SearchSelector/SearchSelector'
import { SEARCH_TYPE_VALUES } from '../SearchSelector/SearchSelector.constants'
import StreamingServiceIcon from '../StreamingServiceIcon/StreamingServiceIcon'
import { OPTION_COMPONENTS } from './autocomplete/plugins'
import { useAutocomplete } from './SearchBox.hooks'
import styles from './SearchBox.module.css'
import type { SearchBoxProps } from './SearchBox.types'

const SearchBox = ({ searchClient }: SearchBoxProps): JSX.Element => {
	const { t } = useTranslation(['search'])

	const {
		iconName,
		inputRef,
		listboxId,
		autocomplete,
		autocompleteState,
		onSearchTypeWillChange,
	} = useAutocomplete(searchClient)

	const searchType = useSearchSelectorStore((state) => state.searchType)
	const placeholderText = SEARCH_TYPE_VALUES.map((type) => [
		type,
		t(`search-selector.${type}-description`),
	])

	const setPlaceholderText = (): string => {
		return placeholderText.find(([type]) => type === searchType)?.[1]
	}

	const totalItemsLength =
		autocompleteState?.collections
			.flatMap((collection) => collection.items)
			.filter((item) => item.objectID !== 'promptAI').length ?? 0

	const focusSearchInput = useCallback((): void => {
		inputRef.current?.focus()
	}, [inputRef])

	return (
		<Container
			maxWidth="large"
			{...(autocomplete.getRootProps() as unknown as React.HTMLAttributes<HTMLDivElement>)}
			aria-controls={listboxId}
		>
			<form
				className={styles.form}
				{...(autocomplete.getFormProps({
					inputElement: inputRef.current,
				}) as unknown as React.FormHTMLAttributes<HTMLFormElement>)}
			>
				<SearchSelector
					onAfterSelect={focusSearchInput}
					onSearchTypeWillChange={onSearchTypeWillChange}
					data-testid="search-mode-selector"
				/>
				<div className={styles.searchField}>
					{iconName && (
						<StreamingServiceIcon className={styles.icon} service={iconName} />
					)}

					<input
						{...(autocomplete.getInputProps({
							inputElement: inputRef.current,
						}) as unknown as React.InputHTMLAttributes<HTMLInputElement>)}
						className={clsx(styles.input, iconName && styles.inputWithIcon)}
						data-testid="search-box"
						placeholder={setPlaceholderText()}
						ref={inputRef}
						//Required for player
						type="search"
						role="searchbox"
					/>

					<button
						aria-label={t('search-button')}
						className={styles.button}
						data-testid="search-box-button"
					>
						<Search color="textPrimary" />
					</button>
				</div>

				{autocompleteState?.isOpen && (
					<div
						className={styles.listbox}
						{...(autocomplete.getPanelProps() as unknown as React.HTMLAttributes<HTMLDivElement>)}
						id={listboxId}
					>
						{autocompleteState?.collections.map((collection) => {
							const { source, items } = collection
							const isEmptyState = source.sourceId === 'emptyStateSource'
							const Option =
								OPTION_COMPONENTS[
									source.sourceId as keyof typeof OPTION_COMPONENTS
								]

							return (
								<ul
									key={source.sourceId}
									{...(autocomplete.getListProps({
										source,
									}) as unknown as React.HTMLAttributes<HTMLUListElement>)}
									aria-label={`${source.sourceId}-list`}
								>
									{items.map((item) => {
										if (isEmptyState) {
											return (
												<Option
													key="empty-state"
													item={item}
													hasSuggestions={totalItemsLength > 0}
													activeItemId={autocompleteState.activeItemId}
													onClose={() => autocomplete.setIsOpen(false)}
												/>
											)
										}

										return (
											<li
												key={item.objectID as string}
												{...(autocomplete.getItemProps({
													item,
													source,
												}) as unknown as React.HTMLAttributes<HTMLLIElement>)}
												role="presentation"
											>
												<Option
													item={item}
													activeItemId={autocompleteState.activeItemId}
													hasSuggestions={totalItemsLength > 0}
												/>
											</li>
										)
									})}
								</ul>
							)
						})}
					</div>
				)}
			</form>
		</Container>
	)
}

export default SearchBox
